import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { KeyFilterModule } from 'primeng/keyfilter';
import { CardModule } from 'primeng/card';
import { Message } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MessagesModule } from 'primeng/messages';
import { 
  Validators, 
  ReactiveFormsModule, 
  FormGroup, 
  AbstractControl, 
  FormsModule, 
  NonNullableFormBuilder, 
  ValidatorFn, 
  ValidationErrors 
} from '@angular/forms';
import { CoreService } from '../../services/core.service';
import { Country } from '../../../shared/models/Countries.model';
import { checkPhoneNumberValidity, numericOnlyInput } from '../../../shared/utils/helpers';
import { Router, RouterModule } from '@angular/router';
import { ForgotPasswordService } from './services/forgot-password.service';
import { ToastService } from '../../services/toast.service';
import { InputOtpChangeEvent, InputOtpModule } from 'primeng/inputotp';
import { PasswordModule } from 'primeng/password';
import { RecaptchaModule } from 'ng-recaptcha';
import { FormControl } from '@angular/forms';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    InputNumberModule,
    InputGroupModule,
    InputGroupAddonModule,
    KeyFilterModule,
    MessagesModule,
    ReactiveFormsModule,
    RouterModule,
    InputOtpModule,
    FormsModule,
    PasswordModule,
    RecaptchaModule
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.sass'
})
export class ForgotPasswordComponent {
  captcha = new FormControl('', Validators.required);
  sitekey = environment.recaptchaSiteKey;
  theme: 'light' | 'dark' = localStorage.getItem('theme') as 'light' | 'dark';
  formData: FormGroup = this.formBuilder.group({
    countryCode: [{name: 'Norway', Alpha2: 'NO', Alpha3: 'NOR', code: '+47'}],
    phoneNumber: [null, Validators.required],
    email: [true, Validators.required]
  });

  passwordForm!: FormGroup;
  countryCodes!: Country[];
  mobileNumber!: string;
  otpValue: string = '';
  isPhoneNumberValid = signal<boolean>(true);
  snackBarErrorMessage: Message[] = [];
  setPage = signal<number>(1);
  loading = signal<boolean>(false);
  sendViaSMS: boolean = true;

  options = [
    { label: 'SMS', icon: 'pi pi-mobile', sendViaSMS: true },
    { label: 'Email', icon: 'pi pi-envelope', sendViaSMS: false }
  ]

  constructor(
    private formBuilder: NonNullableFormBuilder,
    private coreService$: CoreService,
    private forgotPasswordService : ForgotPasswordService,
    public toast: ToastService,
    public router: Router
  ) {
    this.coreService$.countries$.subscribe({
      next: (countries: Country[]) => {
        this.countryCodes = countries
      }
    })

    this.passwordForm = this.formBuilder.group({
      password: ["", [Validators.required, Validators.minLength(8)]],
      confirmPassword: ["", Validators.required],
    }, { validators: this.passwordMatchValidator })
  }

  resolved(captchaResponse: string | null) {
    this.snackBarErrorMessage = [];
    this.captcha.setValue(captchaResponse);
  }

  passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const group = control as FormGroup;
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  get countryCode() {
    return this.formData.controls['countryCode'].value;
  }

  get phoneNumber() {
    return this.formData.controls['phoneNumber'];
  }

  get password() {
    return this.passwordForm.controls['password'];
  }

  get confirmPassword() {
    return this.passwordForm.controls['confirmPassword'];
  }

  sendResetPassword(): void {
    this.loading.set(true);
    const { countryCode, phoneNumber } = this.formData.value;
    this.mobileNumber = countryCode.code + phoneNumber;

    let validity = checkPhoneNumberValidity(String(phoneNumber), countryCode.Alpha2);
    this.isPhoneNumberValid.set(validity);

    if (!validity) {
      this.loading.set(false);
      this.setSnackbarMessage('Invalid phone number!');
    }

    if (this.formData.valid && this.isPhoneNumberValid()) {
      const payload = {
        countryCode: countryCode.code,
        phoneNumber: phoneNumber,
        email: this.sendViaSMS ? '' : 'email',
        captchaToken: this.captcha.value
      }
      this.forgotPasswordService.sendResetCode(payload).subscribe({
        next:(_) => {
          this.loading.set(false);
          this.setPage.set(2);
        },
        error:(error) => {
          this.loading.set(false);
          let trimedMsg = error.message.split(':')[1].trim();
          let message = 'You have already sent a password reset code recently. Please enter the one you already received, or wait a few minutes and try again.'

          if (trimedMsg === message) {
            this.loading.set(false);
            this.setPage.set(2);
            this.setSnackbarMessage(message);
          } else {
            this.setSnackbarMessage(trimedMsg);
          }
        }
      })
    }
  }

  verifyOTP() {
    const { countryCode, phoneNumber } = this.formData.value;
    const errorMessage = [{ severity: 'error', detail: 'Please input OTP' }];
    const payload = {
      countryCode: countryCode.code,
      phoneNumber: phoneNumber,
      resetCode: this.otpValue
    }
    if (!this.otpValue) this.snackBarErrorMessage = errorMessage;
    else {
      this.forgotPasswordService.verifyOTP(payload).subscribe({
        next:() => {
          this.loading.set(false);
          this.setPage.set(3);
        },
        error:(error) => {
          this.loading.set(false);
          this.snackBarErrorMessage = [{ severity: 'error', detail: error.message }]
        }
      })
    }
  }

  submitNewPassword() {
    const { password } = this.passwordForm.value
    const payload = {
      phoneNumber: this.mobileNumber,
      resetCode: this.otpValue,
      newPassword: password
    }

    if (this.passwordForm.valid) {
      this.forgotPasswordService.resetPassword(payload).subscribe({
        next:(_) => {
          this.loading.set(false);
          this.setPage.set(4);
        },
        error:(error) => {
          this.loading.set(false);
          this.snackBarErrorMessage = [{ severity: 'error', detail: error.message }]
        }
      })
    }
  }

  redirectLogin() {
    const { countryCode, phoneNumber } = this.formData.value;
    this.router.navigate(['/login'], {
      queryParams: {
        code: countryCode.code,
        phoneNumber: phoneNumber
      }
    });
  }

  inputNumberOnly(event: KeyboardEvent): void {
    this.snackBarErrorMessage = [];
    this.isPhoneNumberValid.set(true);
    numericOnlyInput(event);
  }

  setSnackbarMessage(detail: string) {
    this.snackBarErrorMessage = [{ severity: 'error', detail: detail}];
  }

  radioButtonChange(event: Event) {
    this.sendViaSMS = event ? true : false;
    this.snackBarErrorMessage = [];
  }

  otpOnChange(event: InputOtpChangeEvent) {
    this.snackBarErrorMessage = []
  }
}
